/* =========================================================================
 * PURSUIT MOVES — the pure model. ZERO IMPORTS so it runs under plain Node.
 * [Jarvis · Claude] 2026-10-26
 *
 * Brian: "there isnt a way to move the pursuit board task status. and you
 * cant really click anything in it either." Measured live the same day:
 * 15,980 records at To Pursue, 1,120 blank, nothing anywhere else.
 *
 * A move is written through POST /api/champion-update (the route the drawer's
 * correction already used; RBAC is the server's), with `pursuitMove: true` so
 * the server reads the real FROM value and lands an append-only ledger row.
 * The card only changes column once the server ECHOES the field back as
 * written — never optimistically on a 200.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()

/** The stages a card can be moved to, in workflow order. */
export const MOVE_STAGES = Object.freeze([
  { key: 'To Pursue', label: 'To Pursue' },
  { key: 'Outreach In Progress', label: 'Outreach In Progress' },
  { key: 'Outreach Completed', label: 'Outreach Completed' },
  { key: 'Won', label: 'Won' },
  { key: 'Champion Crowned', label: 'Champion Crowned' },
])

/** The record's real status — a blank is "Not Started", said as such. */
export const currentStage = (c) => S(c?.pursuit) || 'Not Started'

/**
 * What a rep does NEXT once a card sits in a stage. This is the worklist
 * item a move creates: an action, a reason, and the stage that clears it.
 * `action` names one of the card's existing flows (art / quote / email / win).
 */
export const NEXT_STEP = Object.freeze({
  'Not Started': { action: 'email', label: 'Start outreach', why: 'Nobody has contacted this program yet.', clearsAt: 'Outreach In Progress' },
  'To Pursue': { action: 'email', label: 'Start outreach', why: 'Marked to pursue and not contacted yet.', clearsAt: 'Outreach In Progress' },
  'Outreach In Progress': { action: 'email', label: 'Finish the outreach', why: 'Outreach started. Finish the sequence and log the reply.', clearsAt: 'Outreach Completed' },
  'Outreach Completed': { action: 'quote', label: 'Send the quote', why: 'Outreach is done. A quote is the next move.', clearsAt: 'Won' },
  Won: { action: 'win', label: 'Submit for Win', why: 'Won. Record the win and build the ring art.', clearsAt: 'Champion Crowned' },
  /* Terminal: nothing left to do on the pursuit, so the item CLEARS. */
  'Champion Crowned': null,
})

export const nextStepFor = (stage) => (Object.prototype.hasOwnProperty.call(NEXT_STEP, S(stage))
  ? NEXT_STEP[S(stage)] : NEXT_STEP['Not Started'])

/** The request body. `reason` goes to the permanent ledger. */
export function moveBody(c, to) {
  const from = currentStage(c)
  return {
    id: String(c.id),
    sr_pursuit_status: S(to),
    pursuitMove: true,
    reason: `Pursuit board: ${from} → ${S(to)}`,
  }
}

/**
 * Did the move LAND? Only when the server echoed the field as written. A 200
 * that wrote nothing (`fields: []`, or the field under `ignored`) is a failure
 * with its reason, never a moved card.
 */
export function moveOutcome(res, to, recordId) {
  const fields = Array.isArray(res?.fields) ? res.fields : []
  const journal = res?.pursuitMove
  const journalConfirmed = Number.isSafeInteger(journal?.seq) && journal.seq > 0 && journal.field === 'sr_pursuit_status' && S(journal.to) === S(to) && Number.isFinite(Date.parse(journal.at))
  const echo = res?.properties?.sr_pursuit_status
  if (res?.ok === true && res?.durable === true && journalConfirmed && String(res.id) === String(recordId) && recordId != null
      && fields.includes('sr_pursuit_status') && S(echo) === S(to)) {
    return { landed: true, to: S(to), seq: res?.pursuitMove?.seq ?? null,
      from: res?.pursuitMove?.from ?? null, at: res?.pursuitMove?.at ?? null,
      ledgerMissing: !!res?.pursuitMoveLedgerWarning }
  }
  const ignored = Array.isArray(res?.ignored) && res.ignored.includes('sr_pursuit_status')
  return { landed: false, ledgerMissing: !!res?.pursuitMoveLedgerWarning, reason: ignored
    ? 'The server did not accept the status field, so the card was not moved.'
    : 'The server returned no matching durable status receipt. Refresh the record and inspect history before another attempt.' }
}

/* Brian 2026-10-26: "let them move them but do not let them move them to
 * unassigned." The server enforces it (sr-portal-backend assignments.js);
 * this is the same sentence, for when the server's body is unreadable. */
export const UNASSIGN_EXEC_ONLY = 'Only an executive can leave a card unassigned. Every card must keep a named rep: you can move its pursuit status, and a manager can hand it to another named rep, but no one except an exec can clear the assigned rep. Nothing was changed.'

/** A thrown ApiError → one sentence and what to do about it. */
export function moveFailure(err) {
  const status = err?.status
  if (err?.body?.uncertain === true || err?.body?.retrySafe === false) return 'The move outcome is uncertain. Refresh this record and inspect its move history before another attempt; do not repeat the move blindly.'
  if (err?.notSignedIn || status === 401) return 'You are signed out in this tab. Sign in, then move the card again.'
  /* The server's own sentence wins (assignments.js puts it in `reason`). */
  const said = S(err?.body?.reason || err?.body?.message || err?.body?.detail)
  if (err?.body?.error === 'unassign_exec_only') return said || UNASSIGN_EXEC_ONLY
  if (err?.body?.error === 'legacy_pursuit_move_only') return 'A move can change the pursuit status only. Nothing else on the card was changed, and nothing was moved.'
  if (status === 403) return said || 'Your role cannot move this card. An executive can.'
  if (status === 409 || status === 423) return S(err?.body?.message) || 'This record is locked right now. Nothing was moved.'
  if (!status || status >= 500 || err?.body?.retrySafe === false) return 'The connection or save outcome is uncertain. Refresh this record and read its move history before another attempt; do not repeat the move blindly.'
  return S(err?.body?.message || err?.message) || `The move failed (${status}). Nothing was moved.`
}

/* ---- the worklist half ----------------------------------------------
 * Every move lands on the worklist of the rep who holds the record: the
 * move (who, when, from, to), the next step for the stage it landed in, and
 * the reason. It CLEARS when the record moves on to that step's stage.
 */
export function movesForBook(moves = [], book = [], { now = new Date(), days = 30 } = {}) {
  const byId = new Map(book.map((c) => [String(c.id), c]))
  const cutoff = now.getTime() - days * 86400000
  const latest = new Map()
  for (const m of moves || []) {
    const id = String(m?.recordId ?? '')
    if (!byId.has(id)) continue
    const t = Date.parse(m.at)
    if (!Number.isFinite(t) || t < cutoff) continue
    const prev = latest.get(id)
    if (!prev || Date.parse(prev.at) < t) latest.set(id, m)
  }
  const items = []
  for (const [id, m] of latest) {
    const record = byId.get(id)
    const stage = currentStage(record)
    // The record moved again since (or was changed elsewhere): the board is
    // the truth, so the item follows the record's CURRENT stage.
    const step = nextStepFor(stage)
    if (!step) continue // terminal stage: the work is done, the item clears
    items.push({ id, record, move: m, stage, step, stale: S(m.to) !== stage })
  }
  return items.sort((a, b) => Date.parse(b.move.at) - Date.parse(a.move.at))
}

/** A capped ledger read cannot establish that a book has no recent moves. */
export function pursuitHistoryRead(response) {
  return { moves: Array.isArray(response?.moves) ? response.moves : [], complete: response?.truncated === false && Array.isArray(response?.moves) }
}
